import React, { useState, useEffect } from "react";
import axiosInstance from '../../config/axiosInterceptor';

const fadeInAnim = `
@keyframes fadeInRow {
  0% { opacity: 0; transform: translateY(30px);}
  100% { opacity: 1; transform: translateY(0);}
}
@keyframes totalPop {
  0% { color: #1890ff; transform: scale(1.2);}
  60% { color: #52c41a; }
  100% { color: #1a1a1a; transform: scale(1);}
}
`;

const GamesData = () => {
  const [type, setType] = useState(undefined); // Type: 0, 1, or undefined
  const [transactions, setTransactions] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  // Date filters
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, [type, page, startDate, endDate, limit]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_BASEURL5}/admin/withdrawal/gamesData`, {
        params: { type, page, limit, startDate, endDate },
      });
      const { data, totalAmount, pagination } = response.data;
      setTransactions(data);
      setTotalAmount(totalAmount);
      setTotalPages(Math.ceil(pagination.total / limit));
    } catch (error) {
      setTransactions([]);
      setTotalAmount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  // Helper for animated total amount
  const [displayAmount, setDisplayAmount] = useState(0);
  useEffect(() => {
    let start = displayAmount;
    let end = totalAmount;
    let frame;
    const duration = 600;
    const step = (ts, startTs) => {
      let elapsed = ts - startTs;
      let progress = Math.min(elapsed / duration, 1);
      let val = Math.round(start + (end - start) * progress);
      setDisplayAmount(val);
      if (progress < 1) {
        frame = requestAnimationFrame((now) => step(now, startTs));
      }
    };
    if (start !== end) {
      frame = requestAnimationFrame((now) => step(now, now));
    }
    return () => frame && cancelAnimationFrame(frame);
    // eslint-disable-next-line
  }, [totalAmount]);

  // Main UI
  return (
    <div style={{
      maxWidth: 1100,
      margin: "38px auto",
      background: "#f8fafb",
      borderRadius: 18,
      boxShadow: "0 8px 40px #1677ff22",
      padding: "38px 22px",
      fontFamily: "Inter, Segoe UI, Arial, sans-serif",
      minHeight: 600,
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Animations */}
      <style>{fadeInAnim}</style>
      {/* Heading */}
      <h2 style={{
        textAlign: "center",
        letterSpacing: 1,
        fontWeight: 700,
        fontSize: 32,
        color: "#234",
        marginBottom: 34
      }}>
        Games Transactions
      </h2>
      {/* Filter Bar */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 20,
        alignItems: "flex-end",
        marginBottom: 38,
        justifyContent: "space-between",
        animation: "fadeInRow 0.8s"
      }}>
        {/* Type buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          {[
            { label: "Bet", val: "0", color: "#1677ff" },
            { label: "Result", val: "1", color: "#13c2c2" },
            { label: "All", val: undefined, color: "#999" },
          ].map(btn => (
            <button
              key={btn.label}
              onClick={() => setType(btn.val)}
              style={{
                padding: "8px 30px",
                fontSize: 17,
                borderRadius: 7,
                fontWeight: 600,
                border: "none",
                color: type === btn.val ? "#fff" : "#444",
                background: type === btn.val ? btn.color : "#e6eaf2",
                boxShadow: type === btn.val ? `0 2px 16px ${btn.color}33` : "none",
                cursor: "pointer",
                transition: "all 0.25s"
              }}
            >{btn.label}</button>
          ))}
        </div>
        {/* Date pickers and entries per page */}
        <div style={{
          display: "flex",
          gap: 18,
          alignItems: "center",
          flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ color: "#666", marginBottom: 4, fontSize: 14 }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={e => setStartDate(e.target.value)}
              style={{
                padding: "8px 14px",
                border: "1.5px solid #b8daff",
                borderRadius: 6,
                background: "#fff",
                fontSize: 15,
                outline: "none",
                boxShadow: "0 1px 8px #1890ff12"
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ color: "#666", marginBottom: 4, fontSize: 14 }}>End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              style={{
                padding: "8px 14px",
                border: "1.5px solid #b8daff",
                borderRadius: 6,
                background: "#fff",
                fontSize: 15,
                outline: "none",
                boxShadow: "0 1px 8px #1890ff12"
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ color: "#666", marginBottom: 4, fontSize: 14 }}>Entries/Page</label>
            <select
              value={limit}
              onChange={handleLimitChange}
              style={{
                padding: "8px 14px",
                border: "1.5px solid #b8daff",
                borderRadius: 6,
                background: "#fff",
                fontSize: 15,
                outline: "none",
                boxShadow: "0 1px 8px #1890ff12"
              }}
            >
              {[10, 50, 100, 500].map(n =>
                <option key={n} value={n}>{n}</option>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Animated Total */}
      <div style={{
        background: "#e6f7ff",
        padding: "22px 0",
        borderRadius: 16,
        marginBottom: 18,
        textAlign: "center",
        fontWeight: 700,
        fontSize: 26,
        color: "#1a1a1a",
        letterSpacing: 0.5,
        animation: "totalPop 0.7s"
      }}>
        Total Amount:&nbsp;
        <span style={{
          color: "#1677ff",
          fontSize: 33,
          transition: "color 0.4s"
        }}>
          {displayAmount}
        </span>
      </div>

      {/* Table */}
      <div style={{
        background: "#fff",
        borderRadius: 13,
        boxShadow: "0 4px 16px #1677ff0c",
        overflowX: "auto",
        marginBottom: 26,
        animation: "fadeInRow 0.8s"
      }}>
        {loading ? (
          <div style={{
            textAlign: "center",
            fontSize: 18,
            color: "#888",
            padding: "60px 0"
          }}>
            <div style={{
              width: 48, height: 48, border: "4px solid #1677ff33",
              borderTop: "4px solid #1677ff",
              borderRadius: "50%",
              margin: "0 auto 16px",
              animation: "spin 1s linear infinite"
            }} />
            <style>{`@keyframes spin {100%{transform:rotate(360deg)}}`}</style>
            Loading...
          </div>
        ) : (
          <>
            <table style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 17,
              minWidth: 950
            }}>
              <thead>
                <tr style={{
                  background: "#f0f5ff",
                  color: "#123",
                  fontWeight: 700,
                  textAlign: "center",
                  fontSize: 17
                }}>
                  <th style={{ padding: 14 }}>SNo</th>
                  <th style={{ padding: 14 }}>Date</th>
                  <th style={{ padding: 14 }}>Type</th>
                  <th style={{ padding: 14 }}>User Name</th>
                  <th style={{ padding: 14 }}>Amount</th>
                  <th style={{ padding: 14 }}>Av. Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{
                      textAlign: "center",
                      padding: "60px 0",
                      color: "#bbb",
                      fontSize: 19
                    }}>
                      No data found for this filter.
                    </td>
                  </tr>
                ) : transactions.map((transaction, idx) => (
                  <tr key={transaction._id}
                    style={{
                      background: idx % 2 === 0 ? "#f6fafd" : "#fff",
                      animation: `fadeInRow 0.7s ${(idx + 1) * 60}ms both`
                    }}>
                    <td style={{ padding: 11, textAlign: "center" }}>
                      {(page - 1) * limit + idx + 1}
                    </td>
                    <td style={{ padding: 11 }}>
                      {new Date(transaction.createdAt).toLocaleString()}
                    </td>
                    <td style={{
                      padding: 11,
                      color: transaction.coinPanel === "0" ? "#1677ff" : "#13c2c2",
                      fontWeight: 600
                    }}>
                      {transaction.coinPanel}
                    </td>
                    <td style={{ padding: 11 }}>{transaction?.userId?.username}</td>
                    <td style={{
                      padding: 11,
                      color: "#52c41a",
                      fontWeight: 700
                    }}>
                      {transaction.amount}
                    </td>
                    <td style={{ padding: 11 }}>{transaction.availabeBalance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Pagination */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 12,
        flexWrap: "wrap",
        gap: 14,
        animation: "fadeInRow 0.8s"
      }}>
        <button
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          style={{
            background: page === 1 ? "#e4eaf0" : "#1677ff",
            color: page === 1 ? "#888" : "#fff",
            fontWeight: 600,
            padding: "9px 32px",
            fontSize: 17,
            borderRadius: 6,
            border: "none",
            cursor: page === 1 ? "not-allowed" : "pointer",
            transition: "all 0.2s"
          }}
        >
          Previous
        </button>
        <span style={{ fontSize: 16, fontWeight: 500 }}>
          Page <b>{page}</b> of <b>{totalPages || 1}</b>
        </span>
        <button
          disabled={page === totalPages || totalPages === 0}
          onClick={() => setPage((prev) => prev + 1)}
          style={{
            background: page === totalPages || totalPages === 0 ? "#e4eaf0" : "#1677ff",
            color: page === totalPages || totalPages === 0 ? "#888" : "#fff",
            fontWeight: 600,
            padding: "9px 32px",
            fontSize: 17,
            borderRadius: 6,
            border: "none",
            cursor: page === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
            transition: "all 0.2s"
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default GamesData;
